import { Helmet } from "react-helmet-async";

const SITE = "https://olympusroids.com";
const SITE_NAME = "OLYMPUS MEDS";

export interface SeoProps {
  /** Page-specific title (site name auto-appended unless `exactTitle` is true). */
  title: string;
  /** Meta description, ~155 chars. */
  description: string;
  /** Absolute or root-relative path for canonical. Defaults to current path. */
  path?: string;
  /** If true, use `title` verbatim without appending " — OLYMPUS MEDS". */
  exactTitle?: boolean;
  /** og:image override (absolute URL). Defaults to /og-image.jpg. */
  image?: string;
  /** og:type override. Defaults to "website". */
  type?: "website" | "article" | "product";
  /** noindex, nofollow flag for 404 / placeholder pages. */
  noindex?: boolean;
  /** Optional JSON-LD structured data object(s) to inject. */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** Optional extra keywords (comma-separated). */
  keywords?: string;
}

export default function Seo({
  title,
  description,
  path,
  exactTitle = false,
  image,
  type = "website",
  noindex = false,
  jsonLd,
  keywords,
}: SeoProps) {
  const fullTitle = exactTitle ? title : `${title} — ${SITE_NAME}`;
  const currentPath = path ?? (typeof window !== "undefined" ? window.location.pathname : "/");
  const canonical = `${SITE}${currentPath.startsWith("/") ? currentPath : `/${currentPath}`}`;
  const ogImage = image ?? `${SITE}/og-image.jpg`;

  const jsonLdArray = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
      )}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={fullTitle} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {jsonLdArray.map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
}
